import React, { useState, useEffect } from 'react';
import '../styles/CustomerProfile.css'; // Leverage existing profile CSS for visual consistency
import fetchApi from '../utils/api.js';
import { toast } from 'react-toastify';
import SearchBar from '../components/ui/SearchBar';
import AirDatePicker from '../components/ui/AirDatePicker';

const AccountProfile = ({ account, onBack }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}/${m}/${y}`;
    };

    const formatBalanceValue = (val, isCashOrBank) => {
        const absVal = Math.abs(val);
        const formatted = absVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (isCashOrBank) {
            return val >= 0 ? `${formatted} CR` : `${formatted} DR`;
        } else {
            return val >= 0 ? `${formatted} DR` : `${formatted} CR`;
        }
    };

    const [accountData, setAccountData] = useState(account || null);
    const [transactions, setTransactions] = useState([]);
    const [totals, setTotals] = useState({ debit: 0, credit: 0, balance: 0 });
    const [loading, setLoading] = useState(true);

    // Filter and Pagination States
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    const fetchAccountStatement = async () => {
        try {
            setLoading(true);
            const accId = account?.id || account?._id;
            if (!accId) return;

            let query = `/account/statement/${accId}`;
            const response = await fetchApi(query);

            if (response && response.account) {
                setAccountData(response.account);
                
                let filteredList = response.transactions || [];
                
                // Keep a copy of original transactions to calculate totals based on date filter if active
                setTransactions(filteredList);

                // Compute overall totals
                let deb = 0;
                let cred = 0;
                filteredList.forEach(t => {
                    deb += Number(t.debit) || 0;
                    cred += Number(t.credit) || 0;
                });
                setTotals({
                    debit: deb,
                    credit: cred,
                    balance: deb - cred
                });
            }
        } catch (error) {
            console.error("Error fetching account statement:", error);
            toast.error("Failed to load account statement transactions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (account) {
            fetchAccountStatement();
        }
    }, [account]);

    // Apply front-end filtering for Date and Search Term to keep interaction snappy and robust
    const getFilteredTransactions = () => {
        let list = [...transactions];

        // 1. Date Filter
        if (startDate) {
            const start = new Date(startDate);
            list = list.filter(t => t.date && new Date(t.date) >= start);
        }
        if (endDate) {
            const end = new Date(endDate);
            // set end to end of day to include transactions on that day
            end.setHours(23, 59, 59, 999);
            list = list.filter(t => t.date && new Date(t.date) <= end);
        }

        // 2. Search Text
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(t => 
                (t.particulars && t.particulars.toLowerCase().includes(term)) ||
                (t.type && t.type.toLowerCase().includes(term)) ||
                (t.date && t.date.includes(term))
            );
        }

        return list;
    };

    const filtered = getFilteredTransactions();

    // Re-calculate totals dynamically based on filtered transactions
    const filteredTotals = React.useMemo(() => {
        let deb = 0;
        let cred = 0;
        filtered.forEach(t => {
            deb += Number(t.debit) || 0;
            cred += Number(t.credit) || 0;
        });
        return {
            debit: deb,
            credit: cred,
            balance: deb - cred
        };
    }, [filtered]);

    // Pagination
    const totalEntries = filtered.length;
    const totalPages = Math.ceil(totalEntries / entriesPerPage);
    const lastIdx = currentPage * entriesPerPage;
    const firstIdx = lastIdx - entriesPerPage;
    const currentTransactions = filtered.slice(firstIdx, lastIdx);

    const handleApplyFilter = () => {
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const displayAccountName = accountData?.accountName || accountData?.account_name || account?.partyName || 'Account Statement';
    const displayGroupName = accountData?.group || accountData?.AccountGroup?.account_group_name || 'General';
    const displayGst = accountData?.accountGst || accountData?.account_gst || '---';

    const isCashOrBank = displayGroupName.toLowerCase().includes('bank') || 
                         displayAccountName.toLowerCase().includes('bank') || 
                         displayAccountName.toLowerCase().includes('cash');

    const formatAccountName = (particulars) => {
        if (!isCashOrBank || !particulars) return particulars;
        return particulars.replace(/^(Customer|Seller|Transporter):\s*/i, '');
    };

    return (
        <div className="customer-profile-container">
            {/* Header */}
            <div className="customer-profile-header">
                <button className="customer-profile-back-btn" onClick={onBack}>
                    ← Back to Accounts
                </button>
                <h2 className="customer-profile-title">{displayAccountName} Profile</h2>
                <div style={{ width: '120px' }}></div> {/* Spacer */}
            </div>

            {/* Dynamic Summary Cards */}
            <div className="customer-stats-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginBottom: '25px' }}>
                <div className="customer-stat-card">
                    <div className="customer-stat-icon-wrapper stat-green">
                        ₹
                    </div>
                    <div className="customer-stat-info">
                        <h3>₹{filteredTotals.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                        <p className="customer-stat-label">Total Debit (+)</p>
                    </div>
                </div>
                <div className="customer-stat-card">
                    <div className="customer-stat-icon-wrapper stat-red">
                        ₹
                    </div>
                    <div className="customer-stat-info">
                        <h3>₹{filteredTotals.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                        <p className="customer-stat-label">Total Credit (-)</p>
                    </div>
                </div>
                <div className="customer-stat-card">
                    <div className={`customer-stat-icon-wrapper ${filteredTotals.balance >= 0 ? 'stat-green' : 'stat-red'}`}>
                        ₹
                    </div>
                    <div className="customer-stat-info">
                        <h3>₹{filteredTotals.balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                        <p className="customer-stat-label">Closing Balance</p>
                    </div>
                </div>
            </div>

            {/* Date Filters */}
            <div className="profile-date-filter-row">
                <div className="date-input-group">
                    <label>From Date</label>
                    <AirDatePicker
                        value={startDate}
                        onChange={(dateStr) => setStartDate(dateStr)}
                    />
                </div>
                <div className="date-input-group">
                    <label>To Date</label>
                    <AirDatePicker
                        value={endDate}
                        onChange={(dateStr) => setEndDate(dateStr)}
                    />
                </div>
                <div className="filter-actions-group">
                    <button className="btn-filter-apply" onClick={handleApplyFilter}>Apply</button>
                    <button className="btn-filter-clear" onClick={handleClearFilter}>Clear</button>
                </div>
            </div>

            {/* Main Statement Table */}
            <div className="customer-list-section full-width">
                <div className="customer-list-header-wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                        Ledger Transactions
                    </h3>
                    <div style={{ width: '250px' }}>
                        <SearchBar 
                            placeholder="Search particulars..."
                            value={searchTerm}
                            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        Loading transaction ledger...
                    </div>
                ) : currentTransactions.length === 0 ? (
                    <div className="no-data">No transactions found in this account.</div>
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0', fontFamily: 'inherit' }}>
                            <thead>
                                <tr>
                                    <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'left', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>DATE</th>
                                    {!isCashOrBank && <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'left', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>TYPE</th>}
                                    <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'left', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{isCashOrBank ? 'ACCOUNT NAME' : 'PARTICULARS'}</th>
                                    {isCashOrBank ? (
                                        <>
                                            <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'right', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>CREDIT</th>
                                            <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'right', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>DEBIT</th>
                                        </>
                                    ) : (
                                        <>
                                            <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'right', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>DEBIT</th>
                                            <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'right', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>CREDIT</th>
                                        </>
                                    )}
                                    <th style={{ backgroundColor: '#f8fafc', color: '#475569', fontWeight: '600', padding: '12px 16px', fontSize: '12px', textAlign: 'right', letterSpacing: '0.05em', border: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{isCashOrBank ? 'CLOSING BALANCE' : 'RUNNING BALANCE'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentTransactions.map((t, idx) => (
                                    <tr key={idx}>
                                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'left' }}>{formatDate(t.date)}</td>
                                        {!isCashOrBank && (
                                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                                                <span style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: t.type === 'Expense' ? '#fee2e2' : (t.type === 'Receipt' ? '#dcfce7' : (t.type === 'Payment' ? '#fef3c7' : '#e0f2fe')),
                                                    color: t.type === 'Expense' ? '#dc2626' : (t.type === 'Receipt' ? '#16a34a' : (t.type === 'Payment' ? '#d97706' : '#0284c7'))
                                                }}>
                                                    {t.type}
                                                </span>
                                            </td>
                                        )}
                                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'left' }}>{formatAccountName(t.particulars)}</td>
                                        {isCashOrBank ? (
                                            <>
                                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'right' }}>
                                                    {t.credit > 0 ? t.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                                </td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'right' }}>
                                                    {t.debit > 0 ? t.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'right' }}>
                                                    {t.debit > 0 ? t.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                                </td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'right' }}>
                                                    {t.credit > 0 ? t.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                                </td>
                                            </>
                                        )}
                                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#1e293b', border: '1px solid #e2e8f0', textAlign: 'right' }}>
                                            {formatBalanceValue(t.balance, isCashOrBank)}
                                        </td>
                                    </tr>
                                ))}
                                {/* Bottom Total Row */}
                                <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
                                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'left', fontWeight: 'bold' }}></td>
                                    {!isCashOrBank && <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'left', fontWeight: 'bold' }}></td>}
                                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>TOTAL</td>
                                    {isCashOrBank ? (
                                        <>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>
                                                {filteredTotals.credit > 0 ? filteredTotals.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>
                                                {filteredTotals.debit > 0 ? filteredTotals.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>
                                                {filteredTotals.debit > 0 ? filteredTotals.debit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>
                                                {filteredTotals.credit > 0 ? filteredTotals.credit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                            </td>
                                        </>
                                    )}
                                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#111827', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatBalanceValue(filtered.length > 0 ? filtered[filtered.length - 1].balance : 0, isCashOrBank)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Footer Pagination Toolbar */}
                        <div className="list-page-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px' }}>
                            <div className="list-page-entries-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>Show</span>
                                <select 
                                    value={entriesPerPage} 
                                    onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                    style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <span>entries</span>
                            </div>

                            <div style={{ color: '#4b5563' }}>
                                Showing {firstIdx + 1} to {Math.min(lastIdx, totalEntries)} of {totalEntries} entries
                            </div>

                            <div className="list-page-pagination" style={{ display: 'flex', gap: '5px' }}>
                                <button 
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                    style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: currentPage === 1 ? '#f3f4f6' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentPage(i + 1)}
                                        style={{
                                            padding: '6px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '4px',
                                            backgroundColor: currentPage === i + 1 ? '#0f344d' : '#fff',
                                            color: currentPage === i + 1 ? '#fff' : '#000',
                                            fontWeight: currentPage === i + 1 ? '600' : 'normal',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button 
                                    disabled={currentPage === totalPages || totalPages === 0}
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: (currentPage === totalPages || totalPages === 0) ? '#f3f4f6' : '#fff', cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer' }}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AccountProfile;
